import { useCallback, useState } from 'react';
import { PaginationProps } from 'antd';

type HandlePaginationChange = NonNullable<PaginationProps['onChange']>;

type UserPaginationProps = { page?: number; limit?: number; onChange?: () => void };

type UsePagination = (payload: UserPaginationProps) => {
  page: number;
  limit: number;
  resetPage: () => void;
  handlePaginationChange: HandlePaginationChange;
};

export const usePagination: UsePagination = ({
  page: defaultPage = 1,
  limit: defaultLimit = 10,
  onChange
}) => {
  const [page, setPage] = useState(defaultPage);
  const [limit, setLimit] = useState(defaultLimit);

  const handlePaginationChange: HandlePaginationChange = (pageNumber, pageSize) => {
    /* A page-size change restarts at page 1. The control reports the page the
       user was already on, which a larger size can push past the end of the
       results -- page 20 of 50-row pages is empty once the size becomes 200.
       Changing how many rows you see is not a request to move through them. */
    const sizeChanged = pageSize !== limit;
    setPage(sizeChanged ? 1 : pageNumber);
    setLimit(pageSize);
    onChange?.();
  };

  // Uses the functional updater so `resetPage` keeps a stable identity across
  // renders. Depending on `page` here would make the callback change on every
  // page navigation, which breaks effects that list `resetPage` in their deps.
  const resetPage = useCallback(() => {
    setPage((current) => (current === 1 ? current : 1));
  }, []);

  return {
    page,
    limit,
    resetPage,
    handlePaginationChange
  };
};
